'use client';

import '@ghq-abi/design-system-v2/css';

import { ReactNode, useMemo } from 'react';
import { SessionProvider } from 'next-auth/react';
import { ThemeProvider } from '@ghq-abi/design-system-v2';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { TolgeeProvider, useTolgeeSSR } from '@tolgee/react';

import { AppTemplate } from '~/app/templates/App';
import {
  AbilityProvider,
  AuthProvider,
  FullscreenProvider,
  NavProvider,
  ProfilePictureProvider,
  SocketProvider,
  ToastProvider,
} from '~/contexts';
import { DEFAULT_LANGUAGE } from '~/shared/constants/i18n';
import { SessionAutoRefresh } from '~/shared/hooks';
import { getBrowserQueryClient, getBrowserTolgee, useDatadog } from '~/shared/lib';
import { DatadogAbilitiesTracker } from '~/shared/lib/DatadogAbilitiesTracker';

export function ClientProviders({
  children,
  session,
  language,
}: {
  children: ReactNode;
  session: any;
  language?: string;
}) {
  const queryClient = useMemo(() => getBrowserQueryClient(), []);
  const locale = language ?? DEFAULT_LANGUAGE;
  const tolgee = useMemo(() => getBrowserTolgee(), []);
  const ssrTolgee = useTolgeeSSR(tolgee, locale);

  useDatadog(locale, session);

  return (
    <SessionProvider
      session={session}
      refetchInterval={0}
      refetchOnWindowFocus={false}
    >
      <TolgeeProvider tolgee={ssrTolgee}>
        <QueryClientProvider client={queryClient}>
          <ThemeProvider>
            <ToastProvider>
              <AuthProvider>
                <AbilityProvider>
                <SocketProvider>
                  <FullscreenProvider>
                    <NavProvider>
                      <ProfilePictureProvider>
                        <AppTemplate>
                          <SessionAutoRefresh />
                          <DatadogAbilitiesTracker />
                          {children}
                        </AppTemplate>
                      </ProfilePictureProvider>
                    </NavProvider>
                  </FullscreenProvider>
                </SocketProvider>
                </AbilityProvider>
              </AuthProvider>
            </ToastProvider>
            <ReactQueryDevtools />
          </ThemeProvider>
        </QueryClientProvider>
      </TolgeeProvider>
    </SessionProvider>
  );
}
